/**
 * Tema chiaro/scuro — funzioni pure (nessuna dipendenza da React).
 * Il tema "light" è il default: senza una scelta esplicita in localStorage non cambia nulla.
 *
 * ATTENZIONE: l'elenco LIGHT_ONLY_PREFIXES è duplicato nello script inline di
 * public/index.html (serve a impostare la classe `dark` prima del primo paint).
 * Il test src/context/ThemeContext.test.js verifica che i due elenchi coincidano.
 */
export const THEME_STORAGE_KEY = "affidya-theme";
export const THEMES = ["light", "dark"]; // temi effettivi
export const THEME_PREFERENCES = ["light", "dark", "system"]; // valori ammessi in localStorage
export const DEFAULT_THEME = "light";
// Flag "ha già scelto" (dialog di primo accesso): per browser, accanto a THEME_STORAGE_KEY.
export const THEME_CHOSEN_KEY = "affidya-theme-chosen";
export const DARK_QUERY = "(prefers-color-scheme: dark)";

// Pagine pubbliche / da link (token, registrazione, legali): restano sempre chiare.
export const LIGHT_ONLY_PREFIXES = [
  "/register",
  "/richiedi-demo",
  "/billing",
  "/privacy",
  "/cookies",
  "/terms",
  "/termini-di-servizio",
  "/legal",
  "/preingresso",
  "/terapia",
  "/delega-farmacia",
  "/documenti-firma",
  "/upload",
  "/forgot-password",
  "/reset-password",
  "/family/setup",
  "/attiva-struttura",
  "/upgrade",
  "/trial-expired",
  "/admin-demo",
  // Aree non ancora rifinite/verificate per il tema scuro (nessun interruttore): restano chiare.
  "/sanitario",
  "/sanitario-login",
  "/portale-immobiliare",
];

export function isThemablePath(pathname) {
  const p = String(pathname || "/");
  return !LIGHT_ONLY_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

/** Preferenza salvata: "light" | "dark" | "system" (qualsiasi altro valore => light). */
export function normalizeTheme(value) {
  return THEME_PREFERENCES.includes(value) ? value : DEFAULT_THEME;
}

/**
 * Area Drive (/autista*): preferenza separata, di default "system" (segue il telefono in tempo reale).
 * Separata da `affidya-theme` perché il telefono dell'autista non deve ereditare la scelta fatta
 * dalla struttura sul gestionale (e viceversa). Lo script inline di index.html usa la stessa chiave.
 */
export const DRIVE_THEME_STORAGE_KEY = "drive-theme";
export const DRIVE_DEFAULT_THEME = "system";

export function readStoredDriveTheme() {
  try {
    const v = window.localStorage.getItem(DRIVE_THEME_STORAGE_KEY);
    return THEME_PREFERENCES.includes(v) ? v : DRIVE_DEFAULT_THEME;
  } catch (_e) {
    return DRIVE_DEFAULT_THEME;
  }
}

export function storeDriveTheme(theme) {
  try {
    window.localStorage.setItem(DRIVE_THEME_STORAGE_KEY, THEME_PREFERENCES.includes(theme) ? theme : DRIVE_DEFAULT_THEME);
  } catch (_e) {
    /* storage non disponibile */
  }
}

export function readStoredTheme() {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch (_e) {
    return DEFAULT_THEME;
  }
}

export function storeTheme(theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, normalizeTheme(theme));
  } catch (_e) {
    /* storage non disponibile (modalità privata): la scelta vale solo per la sessione */
  }
}

/** true se l'utente ha già scelto (chiave tema presente oppure flag "chosen"): niente dialog di primo accesso. */
export function hasThemeChoice() {
  try {
    return (
      window.localStorage.getItem(THEME_STORAGE_KEY) !== null ||
      window.localStorage.getItem(THEME_CHOSEN_KEY) === "1"
    );
  } catch (_e) {
    return true; // storage non disponibile: non possiamo ricordare la scelta, meglio non mostrare il dialog a ogni pagina
  }
}

export function storeThemeChosen() {
  try {
    window.localStorage.setItem(THEME_CHOSEN_KEY, "1");
  } catch (_e) {
    /* vedi storeTheme */
  }
}

/** Preferenza del sistema operativo: "dark" | "light", oppure null se non rilevabile (matchMedia assente). */
export function getSystemTheme() {
  try {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
    const mq = window.matchMedia(DARK_QUERY);
    if (!mq || typeof mq.matches !== "boolean") return null;
    return mq.matches ? "dark" : "light";
  } catch (_e) {
    return null;
  }
}

/** Opzione preselezionata nel dialog di primo accesso: quella che combacia con l'OS, altrimenti "light". */
export function detectPreselectedTheme() {
  return getSystemTheme() || DEFAULT_THEME;
}

/** Da preferenza ("light" | "dark" | "system") a tema effettivo ("light" | "dark"). */
export function resolveTheme(preference) {
  if (preference === "system") return getSystemTheme() || DEFAULT_THEME;
  return preference === "dark" ? "dark" : "light";
}

/** "admin" per il pannello super admin (/admin…) e per Le mie visite (/visite), "user" per il resto. */
export function themeAreaForPath(pathname) {
  const p = String(pathname || "/");
  const admin = p === "/admin" || p.startsWith("/admin/") || p.startsWith("/admin-");
  const visite = p === "/visite" || p.startsWith("/visite/") || p === "/visite-login";
  return admin || visite ? "admin" : "user";
}

/** Applica (o toglie) la classe `dark` su <html>, imposta color-scheme e l'area (palette admin / utente). */
export function applyThemeToDocument(effectiveTheme, area = "admin") {
  const root = document.documentElement;
  const dark = effectiveTheme === "dark";
  root.classList.toggle("dark", dark);
  root.style.colorScheme = dark ? "dark" : "";
  root.setAttribute("data-theme-area", area);
}
